import { useEffect, useState } from "react";
import { ArrowDownRight, Play } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { renderRich } from "../lib/rich.jsx";
import ReelModal from "./ReelModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Hero() {
  const { t } = useLang();
  const [reel, setReel] = useState({
    hero_reel_url: "",
    hero_reel_poster: "",
    hero_reel_caption: "",
    hero_bg_video_url: "",
    hero_bg_video_poster: "",
    hero_bg_video_enabled: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings`)
      .then((r) => r.json())
      .then((d) => setReel((prev) => ({ ...prev, ...(d || {}) })))
      .catch(() => {});
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq) {
      setReduceMotion(mq.matches);
      const onChange = (e) => setReduceMotion(e.matches);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, []);

  const hasReel = Boolean(reel.hero_reel_url);
  const hasBgVideo = Boolean(reel.hero_bg_video_enabled && reel.hero_bg_video_url);
  const bgPoster = resolveUrl(reel.hero_bg_video_poster);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[85vh] md:min-h-[70vh] flex items-end overflow-hidden pt-28 md:pt-32 pb-12 md:pb-16"
    >
      {hasBgVideo && (
        <div aria-hidden className="absolute inset-0 overflow-hidden" data-testid="hero-bg-video-layer">
          {reduceMotion ? (
            bgPoster ? (
              <img
                src={bgPoster}
                alt=""
                className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
              />
            ) : null
          ) : (
            <video
              src={resolveUrl(reel.hero_bg_video_url)}
              poster={bgPoster || undefined}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
            />
          )}
          {/* Readability overlays — keep the black + burnt-orange atmosphere */}
          <div className="absolute inset-0 bg-[#0a0a0a]/55 md:bg-[#0a0a0a]/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-transparent to-[#0a0a0a]" />
        </div>
      )}
      <div
        aria-hidden
        className="absolute top-1/3 -right-1/4 w-[60vw] h-[60vw] rounded-full opacity-[0.18] blur-[140px]"
        style={{ background: "#ff4500" }}
      />

      <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12">
        <div className="flex items-center justify-between mb-16 md:mb-24 fade-up">
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            <span className="w-2 h-2 rounded-full bg-[#ff4500] animate-pulse" />
            {t.hero.availability}
          </div>
          <div className="hidden md:block font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            {t.hero.locations}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className={`fade-up ${hasReel ? "lg:col-span-8" : "lg:col-span-12"}`} style={{ animationDelay: "0.15s" }}>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff4500]">
                {t.hero.role}
              </span>
              <span className="h-[1px] w-16 bg-[#262626]" />
            </div>

            <h1
              data-testid="hero-heading"
              className="font-serif tracking-tighter leading-[0.92] text-[14vw] md:text-[10.5vw] lg:text-[8vw] font-light"
            >
              Tico<span className="text-[#ff4500]">.</span>
              <br />
              <span className="italic font-light">Moraes</span>
            </h1>
          </div>

          {hasReel && (
            <div className="lg:col-span-4 fade-up" style={{ animationDelay: "0.3s" }}>
              <button
                data-testid="hero-reel-button"
                onClick={() => setModalOpen(true)}
                className="group relative w-full aspect-[4/5] overflow-hidden bg-[#121212] border border-[#1a1a1a] hover:border-[#ff4500] transition-all duration-500"
                aria-label="Play reel"
              >
                <video
                  src={resolveUrl(reel.hero_reel_url)}
                  poster={resolveUrl(reel.hero_reel_poster)}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full bg-[#ff4500]/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:bg-[#ff4500] transition-all duration-500 shadow-[0_0_40px_rgba(255,69,0,0.4)]">
                    <Play size={26} className="text-black ml-1" fill="currentColor" />
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
                  <span>{reel.hero_reel_caption || "Reel"}</span>
                  <span className="text-[#ff4500]">Play ↗</span>
                </div>
              </button>
            </div>
          )}
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end fade-up" style={{ animationDelay: "0.35s" }}>
          <div className="md:col-span-5 md:col-start-1">
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed max-w-md">
              {renderRich(t.hero.tagline)}
            </p>
          </div>

          <div className="md:col-span-4 md:col-start-7 flex flex-col gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              {t.hero.currentFocus}
            </span>
            <p className="text-white text-base md:text-lg leading-snug">
              {t.hero.currentFocusText}
            </p>
          </div>

          <div className="md:col-span-2 md:col-start-11 flex md:justify-end">
            <a
              href="#work"
              data-testid="hero-scroll-button"
              className="flex items-center gap-3 group"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 group-hover:text-[#ff4500] transition-colors">
                {t.hero.seeWork}
              </span>
              <span className="w-10 h-10 border border-[#262626] group-hover:border-[#ff4500] group-hover:bg-[#ff4500] flex items-center justify-center transition-all duration-300">
                <ArrowDownRight size={16} className="text-white" />
              </span>
            </a>
          </div>
        </div>
      </div>

      <ReelModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        url={reel.hero_reel_url}
        poster={reel.hero_reel_poster}
      />
    </section>
  );
}
